import type { FeatureCollection } from 'geojson';
import type { GeoJSONSource, Map as MlMap } from 'maplibre-gl';
import { useEffect, useRef } from 'react';
import { useMap } from './MapView';

export interface VehicleMarker {
  id: string;
  label: string;
  lat: number;
  lon: number;
  heading: number | null;
  status: string;
  speedKph: number | null;
  /** Glyph family drawn for the marker; derive it with `vehicleKind()` from the vehicle type. */
  kind?: VehicleKind;
}

export type VehicleKind = 'car' | 'truck';

/** Maps a `VehicleType` to the marker glyph: heavy/commercial bodies get the truck, the rest the car. */
export function vehicleKind(type: string | null | undefined): VehicleKind {
  switch (type) {
    case 'TRUCK':
    case 'TRAILER':
    case 'BUS':
    case 'EQUIPMENT':
      return 'truck';
    default:
      return 'car';
  }
}

const STATUS_COLOR: Record<string, string> = { MOVING: '#16A34A', STOPPED: '#94A3B8', IDLE: '#F59E0B', OFFLINE: '#64748B', UNKNOWN: '#94A3B8', ALERT: '#EF4444' };

const SRC = 'vehicles';
/** Id of the vehicle symbol layer — other layers pass it as `beforeId` so vehicles always stay on top. */
export const VEHICLES_LAYER_ID = `${SRC}-sym`;

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const ICON_W = 64;
const ICON_H = 112;
const INK = '#1F2937';
const CAB = '#E5E7EB';
const CAB_ROOF = '#CBD5E1';
const GLASS = '#4B5563';
const LAMP = '#FDE68A';
const TAIL = '#DC2626';

/**
 * Draws a flat top-down vehicle illustration (nose up, status color on the body, dark outline, lighter roof so it
 * reads as 3D) into an ImageData. The map rotates it by the heading, so the nose points where the vehicle goes.
 * `ring` adds a soft selection halo behind the vehicle.
 */
function makeVehicleIcon(kind: VehicleKind, color: string, ring: boolean): ImageData {
  const c = document.createElement('canvas');
  c.width = ICON_W;
  c.height = ICON_H;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, ICON_W, ICON_H);

  if (ring) {
    roundRect(ctx, 2, 2, ICON_W - 4, ICON_H - 4, 28);
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.3;
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  ctx.lineWidth = 3;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = INK;
  if (kind === 'truck') drawTruck(ctx, color);
  else drawCar(ctx, color);

  return ctx.getImageData(0, 0, ICON_W, ICON_H);
}

/** A wheel pair seen from above: dark rounded blocks sticking out of both sides at `y`. */
function wheels(ctx: CanvasRenderingContext2D, y: number, h = 14): void {
  ctx.fillStyle = INK;
  roundRect(ctx, 6, y, 9, h, 3);
  ctx.fill();
  roundRect(ctx, ICON_W - 15, y, 9, h, 3);
  ctx.fill();
}

/** Drop shadow for the body so it sits on the map like a solid object. */
function bodyShadow(ctx: CanvasRenderingContext2D, on: boolean): void {
  ctx.shadowColor = on ? 'rgba(15,42,28,.45)' : 'transparent';
  ctx.shadowBlur = on ? 5 : 0;
  ctx.shadowOffsetX = on ? 2 : 0;
  ctx.shadowOffsetY = on ? 3 : 0;
}

/** Box truck from above, nose up: light cab with a dark windshield, cargo box in the status color, three axles. */
function drawTruck(ctx: CanvasRenderingContext2D, color: string): void {
  wheels(ctx, 20);
  wheels(ctx, 68);
  wheels(ctx, 86);
  // cab
  bodyShadow(ctx, true);
  roundRect(ctx, 12, 5, 40, 32, 7);
  ctx.fillStyle = CAB;
  ctx.fill();
  bodyShadow(ctx, false);
  ctx.stroke();
  // windshield + cab roof
  ctx.fillStyle = GLASS;
  roundRect(ctx, 16, 11, 32, 11, 3);
  ctx.fill();
  ctx.fillStyle = CAB_ROOF;
  roundRect(ctx, 16, 25, 32, 8, 2);
  ctx.fill();
  // headlights
  ctx.fillStyle = LAMP;
  roundRect(ctx, 15, 6, 9, 3, 1.5);
  ctx.fill();
  roundRect(ctx, 40, 6, 9, 3, 1.5);
  ctx.fill();
  // cargo box
  bodyShadow(ctx, true);
  roundRect(ctx, 10, 40, 44, 66, 5);
  ctx.fillStyle = color;
  ctx.fill();
  bodyShadow(ctx, false);
  ctx.stroke();
  // box roof panel (lighter) + rear edge (darker)
  ctx.fillStyle = 'rgba(255,255,255,.22)';
  roundRect(ctx, 15, 45, 34, 52, 3);
  ctx.fill();
  ctx.fillStyle = 'rgba(0,0,0,.28)';
  roundRect(ctx, 14, 100, 36, 3, 1.5);
  ctx.fill();
}

/** Sedan from above, nose up: body in the status color, dark windshield and rear window, lighter roof, two axles. */
function drawCar(ctx: CanvasRenderingContext2D, color: string): void {
  wheels(ctx, 22, 16);
  wheels(ctx, 72, 16);
  // body
  bodyShadow(ctx, true);
  roundRect(ctx, 10, 6, 44, 100, 15);
  ctx.fillStyle = color;
  ctx.fill();
  bodyShadow(ctx, false);
  ctx.stroke();
  // windshield, roof, rear window
  ctx.fillStyle = GLASS;
  ctx.beginPath();
  ctx.moveTo(17, 30);
  ctx.lineTo(47, 30);
  ctx.lineTo(45, 46);
  ctx.lineTo(19, 46);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,.25)';
  roundRect(ctx, 19, 48, 26, 28, 4);
  ctx.fill();
  ctx.fillStyle = GLASS;
  ctx.beginPath();
  ctx.moveTo(19, 78);
  ctx.lineTo(45, 78);
  ctx.lineTo(47, 90);
  ctx.lineTo(17, 90);
  ctx.closePath();
  ctx.fill();
  // lights
  ctx.fillStyle = LAMP;
  roundRect(ctx, 14, 8, 11, 4, 2);
  ctx.fill();
  roundRect(ctx, 39, 8, 11, 4, 2);
  ctx.fill();
  ctx.fillStyle = TAIL;
  roundRect(ctx, 14, 100, 11, 3, 1.5);
  ctx.fill();
  roundRect(ctx, 39, 100, 11, 3, 1.5);
  ctx.fill();
}

function ensureIcons(map: MlMap): void {
  for (const kind of ['car', 'truck'] as const) {
    for (const [status, color] of Object.entries(STATUS_COLOR)) {
      if (!map.hasImage(`veh-${kind}-${status}`)) map.addImage(`veh-${kind}-${status}`, makeVehicleIcon(kind, color, false), { pixelRatio: 2 });
      if (!map.hasImage(`veh-${kind}-sel-${status}`)) map.addImage(`veh-${kind}-sel-${status}`, makeVehicleIcon(kind, color, true), { pixelRatio: 2 });
    }
  }
}

/** Where a marker is drawn right now: its target or an in-flight interpolation towards it. */
interface Shown {
  lat: number;
  lon: number;
  /** Degrees clockwise from north; the reported heading, else the direction of the last move. */
  heading: number;
}

export function vehiclesToGeoJSON(items: VehicleMarker[], selectedId?: string | null, shown?: Map<string, Shown>): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: items.map((v) => {
      const status = STATUS_COLOR[v.status] ? v.status : 'UNKNOWN';
      const selected = v.id === selectedId;
      const at = shown?.get(v.id);
      return {
        type: 'Feature',
        id: v.id,
        properties: { id: v.id, label: v.label, status, heading: at?.heading ?? v.heading ?? 0, icon: `veh-${v.kind ?? 'car'}${selected ? '-sel' : ''}-${status}`, selected: selected ? 1 : 0 },
        geometry: { type: 'Point', coordinates: [at?.lon ?? v.lon, at?.lat ?? v.lat] },
      };
    }),
  };
}

/** Markers glide between fixes: at least this long, at most this long, otherwise the gap since the previous fix. */
const MOVE_MIN_MS = 600;
const MOVE_MAX_MS = 8000;

interface Motion {
  from: { lat: number; lon: number; heading: number };
  to: { lat: number; lon: number; heading: number };
  start: number;
  dur: number;
  /** When the current target arrived, to pace the next move like the feed. */
  arrivedAt: number;
}

/** Initial bearing from a to b in degrees clockwise from north, or null when the points coincide. */
function bearing(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number | null {
  if (Math.abs(a.lat - b.lat) < 1e-7 && Math.abs(a.lon - b.lon) < 1e-7) return null;
  const φ1 = (a.lat * Math.PI) / 180, φ2 = (b.lat * Math.PI) / 180, Δλ = ((b.lon - a.lon) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function shownPoint(m: Motion, now: number): { lat: number; lon: number; heading: number } {
  if (m.dur <= 0 || now >= m.start + m.dur) return m.to;
  // Linear so a steady feed reads as steady motion (the next fix usually lands as this move ends).
  const k = (now - m.start) / m.dur;
  // Turn the short way round.
  const dh = ((m.to.heading - m.from.heading + 540) % 360) - 180;
  return { lat: m.from.lat + (m.to.lat - m.from.lat) * k, lon: m.from.lon + (m.to.lon - m.from.lon) * k, heading: (m.from.heading + dh * k + 360) % 360 };
}

export function VehiclesLayer({ vehicles, selectedId, onClick, zoomOnClick = false }: { vehicles: VehicleMarker[]; selectedId?: string | null; onClick?: (id: string) => void; zoomOnClick?: boolean }) {
  const map = useMap();
  const motions = useRef(new Map<string, Motion>());
  const raf = useRef(0);

  // Data + smooth movement: new fixes become animation targets and the source is re-set every frame while anything moves.
  useEffect(() => {
    if (!map) return;
    ensureIcons(map);
    const now = performance.now();
    const seen = new Set<string>();
    for (const v of vehicles) {
      seen.add(v.id);
      const m = motions.current.get(v.id);
      if (!m) {
        const at = { lat: v.lat, lon: v.lon, heading: v.heading ?? 0 };
        motions.current.set(v.id, { from: at, to: at, start: now, dur: 0, arrivedAt: now });
        continue;
      }
      const target = { lat: v.lat, lon: v.lon, heading: v.heading ?? bearing(m.to, v) ?? m.to.heading };
      if (m.to.lat === target.lat && m.to.lon === target.lon && m.to.heading === target.heading) continue;
      const cur = shownPoint(m, now);
      const moved = m.to.lat !== target.lat || m.to.lon !== target.lon;
      const dur = moved ? Math.min(MOVE_MAX_MS, Math.max(MOVE_MIN_MS, now - m.arrivedAt)) : MOVE_MIN_MS;
      motions.current.set(v.id, { from: cur, to: target, start: now, dur, arrivedAt: moved ? now : m.arrivedAt });
    }
    for (const id of [...motions.current.keys()]) if (!seen.has(id)) motions.current.delete(id);

    const paint = () => {
      const t = performance.now();
      const shown = new Map<string, Shown>();
      let moving = false;
      for (const [id, m] of motions.current) {
        const p = shownPoint(m, t);
        shown.set(id, p);
        if (t < m.start + m.dur) moving = true;
      }
      const data = vehiclesToGeoJSON(vehicles, selectedId, shown);
      const src = map.getSource(SRC) as GeoJSONSource | undefined;
      if (src) src.setData(data);
      else map.addSource(SRC, { type: 'geojson', data });
      raf.current = moving ? requestAnimationFrame(paint) : 0;
    };
    cancelAnimationFrame(raf.current);
    paint();
    return () => cancelAnimationFrame(raf.current);
  }, [map, vehicles, selectedId]);

  // Layer + click handling.
  useEffect(() => {
    if (!map) return;
    ensureIcons(map);
    if (!map.getSource(SRC)) map.addSource(SRC, { type: 'geojson', data: vehiclesToGeoJSON([]) });
    if (!map.getLayer(VEHICLES_LAYER_ID)) {
      map.addLayer({
        id: VEHICLES_LAYER_ID,
        type: 'symbol',
        source: SRC,
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': ['case', ['==', ['get', 'selected'], 1], 1.1, 0.9],
          'icon-rotate': ['get', 'heading'],
          'icon-rotation-alignment': 'map',
          'text-rotation-alignment': 'viewport',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'symbol-sort-key': ['case', ['==', ['get', 'selected'], 1], 0, 1],
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-font': ['Noto Sans Bold'],
          'text-offset': [0, 1.9],
          'text-anchor': 'top',
          'text-allow-overlap': false,
          'text-optional': true,
        },
        paint: { 'text-color': '#0F2A1C', 'text-halo-color': '#ffffff', 'text-halo-width': 1.6 },
      });
      map.on('mouseenter', VEHICLES_LAYER_ID, () => (map.getCanvas().style.cursor = 'pointer'));
      map.on('mouseleave', VEHICLES_LAYER_ID, () => (map.getCanvas().style.cursor = ''));
    }
    const handler = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      const f = e.features?.[0];
      const id = f?.properties?.['id'] as string | undefined;
      if (!id) return;
      if (zoomOnClick && f?.geometry.type === 'Point') {
        const [lon, lat] = f.geometry.coordinates as [number, number];
        map.flyTo({ center: [lon, lat], zoom: Math.max(map.getZoom(), 14), duration: 600 });
      }
      onClick?.(id);
    };
    map.on('click', VEHICLES_LAYER_ID, handler);
    return () => {
      map.off('click', VEHICLES_LAYER_ID, handler);
    };
  }, [map, onClick, zoomOnClick]);

  return null;
}
